import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ScanLine, AlertTriangle, FileUp, History, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";

const fmtM = (n) => (n == null ? "-" : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : formatCurrency(n));

function SheetResult({ r }) {
  const t = r.terms || {};
  const m = r.math;
  return (
    <div className="rounded-lg border p-4" data-testid={`xray-result-${r.label}`}>
      <div className="mb-1 text-sm font-bold text-foreground">{r.label}</div>
      {t.summary && <p className="mb-3 text-xs text-muted-foreground">{t.summary}</p>}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {[["Instrument", t.instrument || "?"], ["Investment", fmtM(t.investment)],
          ["Post-money", fmtM(m?.post_money ?? t.post_money)], ["Investor gets", m ? `${m.investor_pct}%` : "-"],
          ["New pool", m?.new_pool_pct ? `${m.new_pool_pct}%` : "-"],
          ["Liq pref", t.liq_pref_multiple ? `${t.liq_pref_multiple}x${t.participating ? " participating" : ""}` : "1x (assumed)"],
          ["Anti-dilution", t.anti_dilution || "-"],
          ["Your dilution", m ? `-${m.founder_dilution_pts} pts (${m.founder_pct_pre}% → ${m.founder_pct_post}%)` : "-"],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-dashed py-1">
            <span className="text-muted-foreground">{k}</span><span className="font-semibold text-foreground">{v}</span>
          </div>
        ))}
      </div>
      {m && (
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Your take-home at exit</div>
          <div className="grid grid-cols-3 gap-2">
            {m.exits.map((e) => (
              <div key={e.exit_multiple} className="rounded-md bg-secondary/50 p-2 text-center">
                <div className="text-[10px] text-muted-foreground">{e.exit_multiple}x exit ({fmtM(e.exit_value)})</div>
                <div className="text-sm font-bold text-foreground">{fmtM(e.founder_take)}</div>
                <div className="text-[10px] text-muted-foreground">investor {fmtM(e.investor_take)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {(t.watchouts || []).length > 0 && (
              <div data-testid="termsheet-watchouts" className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5">
                <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-800"><AlertTriangle className="h-3 w-3" /> Watchouts</div>
          <ul className="space-y-0.5 text-xs text-foreground">{t.watchouts.map((w, i) => <li key={i}>• {w}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

export const TermsheetXray = ({ open, onClose, companyId }) => {
  const [sheets, setSheets] = useState([{ label: "", text: "" }, { label: "", text: "" }]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRefs = [useRef(null), useRef(null)];

  useEffect(() => {
    if (open && companyId) {
      api.get(`/companies/${companyId}/termsheet-xrays`).then(({ data }) => setHistory(data)).catch(() => {});
    }
  }, [open, companyId]);

  const deleteRun = async (id) => {
    try {
      await api.delete(`/termsheet-xrays/${id}`);
      setHistory((h) => h.filter((r) => r.id !== id));
    } catch { toast.error("Could not delete"); }
  };

  const uploadPdf = async (i, file) => {
    if (!file) return;
    setUploading(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/companies/${companyId}/termsheet-pdf-text`, fd);
      setSheet(i, { text: data.text, label: sheets[i].label || file.name.replace(/\.pdf$/i, "") });
      toast.success(`Extracted text from ${data.filename}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read that PDF"); }
    finally { setUploading(null); if (fileRefs[i].current) fileRefs[i].current.value = ""; }
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/termsheet-xray`, {
        sheets: sheets.filter((s) => s.text.trim()),
      });
      setResult(data);
      api.get(`/companies/${companyId}/termsheet-xrays`).then(({ data }) => setHistory(data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "X-Ray failed"); }
    finally { setBusy(false); }
  };
  const setSheet = (i, patch) => setSheets((arr) => arr.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl" data-testid="xray-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="h-4 w-4 text-primary" /> Term Sheet X-Ray</DialogTitle>
          <DialogDescription>Paste one or two term sheets (or SAFEs). AI extracts the terms, then we run the real dilution and exit math on your actual cap table.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-4">
            {sheets.map((s, i) => (
              <div key={i}>
                <div className="mb-1.5 flex items-center gap-1.5">
                  <Input value={s.label} onChange={(e) => setSheet(i, { label: e.target.value })}
                    placeholder={`Sheet ${i === 0 ? "A" : "B"} label (e.g. "Meridian offer")${i === 1 ? " - optional" : ""}`}
                    className="h-8 flex-1 text-xs" data-testid={`xray-label-${i}`} />
                  <input ref={fileRefs[i]} type="file" accept=".pdf,application/pdf" className="hidden"
                    onChange={(e) => uploadPdf(i, e.target.files?.[0])} data-testid={`xray-file-${i}`} />
                  <Button type="button" variant="outline" size="sm" className="h-8 shrink-0 gap-1 px-2.5 text-[11px]"
                    disabled={uploading !== null} onClick={() => fileRefs[i].current?.click()} data-testid={`xray-upload-${i}`}>
                    {uploading === i ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileUp className="h-3 w-3" />} PDF
                  </Button>
                </div>
                <Textarea value={s.text} onChange={(e) => setSheet(i, { text: e.target.value })}
                  placeholder={i === 0 ? "Paste the term sheet text here, or upload the PDF…" : "Optional: paste or upload a second offer to compare…"}
                  rows={5} className="text-xs" data-testid={`xray-text-${i}`} />
              </div>
            ))}
            <Button onClick={run} disabled={busy || !sheets[0].text.trim()} className="w-full gap-2" data-testid="xray-run-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />}
              {busy ? "Reading the fine print…" : "Run X-Ray"}
            </Button>
            {history.length > 0 && (
              <div className="rounded-lg border bg-secondary/30 p-3" data-testid="xray-history">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground"><History className="h-3.5 w-3.5 text-primary" /> Past X-Rays</div>
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {history.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-xs" data-testid={`xray-history-${r.id}`}>
                      <button className="min-w-0 flex-1 truncate rounded px-1.5 py-1 text-left font-medium text-foreground hover:bg-secondary"
                        onClick={() => setResult(r)} data-testid={`xray-history-open-${r.id}`}>
                        {(r.results || []).map((x) => x.label).join(" vs ")}
                        <span className="ml-2 text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                      </button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteRun(r.id)} data-testid={`xray-history-delete-${r.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`grid gap-3 ${result.results.length > 1 ? "md:grid-cols-2" : ""}`}>
              {result.results.map((r) => <SheetResult key={r.label} r={r} />)}
            </div>
            <p className="text-[10px] text-muted-foreground">{result.model_note} Not legal advice.</p>
            <Button variant="outline" onClick={() => setResult(null)} className="w-full" data-testid="xray-again-btn">Compare different sheets</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
